import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type CreateOwnerBlockInput, type PropertyBlocks } from '@/api/client'
import { portfolioCalendarKey } from '@/hooks/use-portfolio-calendar'
import { propertyCalendarKey } from '@/hooks/use-property-calendar'

/** How often the blocks are re-read while Uplisting is still applying one. */
export const BLOCKS_SETTLE_POLL_MS = 8_000

export function propertyBlocksKey(propertyId: string | null | undefined) {
  return ['property-blocks', propertyId] as const
}

/** True while any owner block is waiting on Uplisting to confirm a create or a removal. */
export function hasUnsettledBlock(data: PropertyBlocks | undefined): boolean {
  return (data?.owner_blocks ?? []).some(
    block => block.sync_status === 'pending' || block.sync_status === 'deleting'
  )
}

/**
 * The property's owner blocks and Uplisting-app holds in full
 * (`GET /admin/properties/:id/blocks`). The calendar read carries each block's
 * dates and status for the grid; this carries what the panels show (cleaning,
 * reason, errors). Re-read every few seconds while a block is still syncing, so
 * the panel moves on from "pending" without a page reload.
 */
export function usePropertyBlocks(
  propertyId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: propertyBlocksKey(propertyId),
    queryFn: () => api.getPropertyBlocks(propertyId as string),
    enabled: !!propertyId && enabled,
    staleTime: 15_000,
    refetchInterval: q => (hasUnsettledBlock(q.state.data) ? BLOCKS_SETTLE_POLL_MS : false),
  })

  // The calendar reads do not poll. When the last syncing block settles (a
  // removal drops out of the list, a create turns synced), the property's grid
  // and the portfolio calendar are stale by exactly that change, so both are
  // read again.
  const unsettled = hasUnsettledBlock(query.data)
  const wasUnsettled = useRef(false)
  useEffect(() => {
    if (wasUnsettled.current && !unsettled) {
      void queryClient.invalidateQueries({ queryKey: propertyCalendarKey(propertyId) })
      void queryClient.invalidateQueries({ queryKey: portfolioCalendarKey() })
    }
    wasUnsettled.current = unsettled
  }, [unsettled, propertyId, queryClient])

  return query
}

/**
 * Everything a block write can change on screen: the blocks list, every month
 * of the property's calendar, and every loaded window of the portfolio
 * calendar (which draws the same block on that property's row).
 */
function useRefreshBlocks(propertyId: string) {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: propertyBlocksKey(propertyId) }),
      queryClient.invalidateQueries({ queryKey: propertyCalendarKey(propertyId) }),
      queryClient.invalidateQueries({ queryKey: portfolioCalendarKey() }),
    ])
}

export function useCreateOwnerBlock(propertyId: string) {
  const refresh = useRefreshBlocks(propertyId)
  return useMutation({
    mutationFn: (input: CreateOwnerBlockInput) => api.createOwnerBlock(propertyId, input),
    // Nothing is drawn before the server answers: the grid shows what the API
    // returns, so the new block appears with the refetch.
    onSuccess: () => refresh(),
  })
}

export function useReleaseOwnerBlock(propertyId: string) {
  const refresh = useRefreshBlocks(propertyId)
  return useMutation({
    mutationFn: (blockId: string) => api.releaseOwnerBlock(propertyId, blockId),
    onSuccess: () => refresh(),
  })
}

/** Live availability of one hold's range. A read, run on demand, never cached. */
export function useCheckHold(propertyId: string) {
  return useMutation({
    mutationFn: (range: { start: string; end: string }) =>
      api.checkExternalBlock(propertyId, range),
  })
}
